import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import QuizComponent from '../../components/QuizComponent';
import { Library, BookOpen, FileText, ExternalLink, BookMarked, Video, Headphones, AlertTriangle, CheckCircle } from 'lucide-react';

const resources = {
  graceBooks: [
    {
      title: 'The Finished Work of Christ',
      author: 'Andrew Farley',
      description: 'Clear teaching on what Jesus accomplished and how it changes everything about our relationship with God.',
      category: 'Grace Foundation'
    },
    {
      title: 'Classic Christianity',
      author: 'Bob George',
      description: 'Life\'s too short to miss the real thing. Understanding the New Covenant and your identity in Christ.',
      category: 'Identity'
    },
    {
      title: 'Grace Walk',
      author: 'Steve McVey',
      description: 'What you\'ve always wanted in the Christian life but never knew how to find.',
      category: 'Christian Living'
    },
    {
      title: 'So You Don\'t Want to Go to Church Anymore',
      author: 'Wayne Jacobsen & Dave Coleman',
      description: 'A story about finding authentic faith outside religious performance.',
      category: 'Freedom'
    },
    {
      title: 'Destined to Reign',
      author: 'Joseph Prince',
      description: 'The secret to effortless success, wholeness, and victorious living.',
      category: 'Grace Living'
    },
    {
      title: 'The Naked Gospel',
      author: 'Andrew Farley',
      description: 'The truth you may never hear in church. Pure grace without religious mixture.',
      category: 'Gospel Clarity'
    }
  ],
  studyTools: [
    {
      title: 'ESV Study Bible',
      description: 'Comprehensive study notes. Filter through New Covenant lens when reading OT application.',
      type: 'Study Bible'
    },
    {
      title: 'Blue Letter Bible',
      description: 'Free online resource for original language study. Helpful for understanding Greek words.',
      type: 'Online Tool',
      link: 'https://www.blueletterbible.org'
    },
    {
      title: 'Bible Hub Interlinear',
      description: 'See the Greek text alongside translations. Great for word studies.',
      type: 'Online Tool',
      link: 'https://biblehub.com/interlinear'
    }
  ],
  graceMinistries: [
    {
      title: 'The Grace Message',
      description: 'Andrew Farley\'s ministry focused on the finished work and identity in Christ.',
      type: 'Podcast/Teaching',
      link: 'https://www.thegracemessage.com'
    },
    {
      title: 'Grace Based Bible Studies',
      description: 'Bob George\'s ministry continuing to spread Classic Christianity message.',
      type: 'Teaching',
      link: 'https://www.realanswers.net'
    }
  ],
  evaluationCriteria: [
    {
      check: 'Does it teach total forgiveness (past, present, future)?',
      anchor: 'Colossians 2:13-14'
    },
    {
      check: 'Does it affirm permanent union with Christ?',
      anchor: '1 Corinthians 6:17'
    },
    {
      check: 'Does it teach freedom from the Law?',
      anchor: 'Romans 6:14; Galatians 5:18'
    },
    {
      check: 'Does it avoid "two natures" or "wicked heart" language?',
      anchor: '2 Corinthians 5:17; Ezekiel 36:26'
    },
    {
      check: 'Does it motivate with love/gratitude, not fear/guilt?',
      anchor: '1 John 4:18; Romans 8:1'
    },
    {
      check: 'Does it point to resting in Christ, not striving?',
      anchor: 'Matthew 11:28-30; Galatians 2:20'
    }
  ]
};

export default function ResourcesHub() {
  return (
    <div className="min-h-[calc(100vh-64px)]" data-testid="resources-hub">
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1695730866897-c7dbbdef09b0?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Books on table representing knowledge and tools"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hub-hero-overlay flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 w-full">
            <span className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm mb-3">
              Step 6 · Multiplication
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Resources
            </h1>
            <p className="text-white/80 mt-2 max-w-2xl">
              Grace-centered tools and materials
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="evaluate" className="space-y-8">
          <TabsList className="grid w-full max-w-xl grid-cols-4">
            <TabsTrigger value="evaluate" data-testid="tab-evaluate">Evaluate</TabsTrigger>
            <TabsTrigger value="books" data-testid="tab-books">Books</TabsTrigger>
            <TabsTrigger value="tools" data-testid="tab-tools">Tools</TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz">Quiz</TabsTrigger>
          </TabsList>

          <TabsContent value="evaluate" className="space-y-8">
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed">
                Not all Christian resources teach grace clearly. Many mix law and grace, imply conditional 
                forgiveness, or use fear to motivate. Use these criteria to evaluate any teaching.
              </p>
            </div>

            {/* Evaluation Card */}
            <Card className="max-w-3xl">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  Grace-Centered Resource Checklist
                </CardTitle>
                <CardDescription>
                  A resource that teaches New Covenant truth should pass ALL of these
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {resources.evaluationCriteria.map((criterion, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">{criterion.check}</p>
                      <p className="text-sm text-primary">{criterion.anchor}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Warning Card */}
            <Card className="max-w-3xl bg-destructive/5 border-destructive/20">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                  Red Flags to Watch For
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  'Teaching that believers can be "out of fellowship" with God',
                  'Instructions to "confess sins to be cleansed/restored"',
                  'Emphasis on the Ten Commandments as Christian rule of life',
                  'Fear-based motivation ("God is disappointed in you")',
                  'Language about your "wicked heart" or "sin nature"',
                  'Graded rewards in heaven based on performance',
                  'Implying distance from God that must be bridged by you',
                ].map((flag, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive mt-1 flex-shrink-0" />
                    <p className="text-sm text-foreground/80">{flag}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="books" className="space-y-6">
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed">
                These books clearly teach total forgiveness, permanent union, freedom from law, and 
                grace-empowered living. They avoid the mixture that confuses many believers.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.graceBooks.map((book, index) => (
                <Card key={book.title} className="hub-card animate-slide-up" style={{ animationDelay: `${index * 50}ms` }}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <BookOpen className="h-8 w-8 text-primary" strokeWidth={1.5} />
                      <span className="text-xs bg-muted px-2 py-1 rounded-full">{book.category}</span>
                    </div>
                    <CardTitle className="font-serif text-lg">{book.title}</CardTitle>
                    <CardDescription>by {book.author}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-foreground/80">{book.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="tools" className="space-y-6">
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed">
                These study tools help with language study and research. Remember: always filter through 
                the New Covenant lens when reading commentaries.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {resources.studyTools.map((tool, index) => (
                <Card key={tool.title} className="hub-card animate-slide-up" style={{ animationDelay: `${index * 50}ms` }}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <FileText className="h-8 w-8 text-primary" strokeWidth={1.5} />
                      <span className="text-xs bg-muted px-2 py-1 rounded-full">{tool.type}</span>
                    </div>
                    <CardTitle className="font-serif text-lg">{tool.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-foreground/80">{tool.description}</p>
                    {tool.link && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={tool.link} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Visit Site
                        </a>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <h3 className="font-serif text-xl font-semibold">Grace-Focused Ministries</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {resources.graceMinistries.map((ministry, index) => (
                <Card key={ministry.title} className="hub-card">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <Headphones className="h-8 w-8 text-primary" strokeWidth={1.5} />
                      <span className="text-xs bg-muted px-2 py-1 rounded-full">{ministry.type}</span>
                    </div>
                    <CardTitle className="font-serif text-lg">{ministry.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-foreground/80">{ministry.description}</p>
                    <Button variant="outline" size="sm" asChild>
                      <a href={ministry.link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Visit Site
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="quiz">
            <div className="max-w-2xl mx-auto">
              <QuizComponent hubId="resources" />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
